/**
 * Checkout-specific address service for Loqate integration
 * Handles address lookup and manual entry toggle for checkout forms
 */

export interface AddressData {
    id: string;
    text: string;
    description?: string;
    type?: string;
    isContainer?: boolean;
}

export interface FullAddress {
    line1: string;
    line2: string;
    line3: string;
    city: string;
    provinceName: string;
    postalCode: string;
    countryName: string;
}

export class CheckoutAddressService {
    private locationTimer: { [key: string]: number } = {};
    private lastQuery: { [key: string]: string } = {};

    public initialize(): void {
        this.bindEventHandlers();
        
        const sameAsDeliveryCheckbox = document.getElementById("SameAsDelivery") as HTMLInputElement;
        if (sameAsDeliveryCheckbox) {
            this.handleSameAsDelivery(sameAsDeliveryCheckbox.checked);
        }
    }

    private bindEventHandlers(): void {
        // Toggle to manual entry
        document.addEventListener("click", (event) => {
            const target = event.target as Element;
            if (target.matches(".js-checkout-address-manual-toggle")) {
                event.preventDefault();
                const button = target as HTMLButtonElement;
                const targetPrefix = button.dataset.target;
                if (targetPrefix) {
                    this.showManualEntry(targetPrefix);
                }
            }
        });

        // Toggle back to lookup
        document.addEventListener("click", (event) => {
            const target = event.target as Element;
            if (target.matches(".js-checkout-address-lookup-toggle")) {
                event.preventDefault();
                const button = target as HTMLButtonElement;
                const targetPrefix = button.dataset.target;
                if (targetPrefix) {
                    this.showAddressLookup(targetPrefix);
                }
            }
        });

        // Address lookup input
        document.addEventListener("input", (event) => {
            const target = event.target as Element;
            if (target.matches(".addresslookup-container .addresslookup-textsearch")) {
                this.handleAddressLookupInput(event);
            }
        });

        // Address selection
        document.addEventListener("click", (event) => {
            const target = event.target as Element;
            if (target.matches(".addresslookup-results-list li")) {
                this.handleAddressSelection(event);
            }
        });

        // Same as delivery checkbox
        const sameAsDeliveryCheckbox = document.getElementById("SameAsDelivery") as HTMLInputElement;
        if (sameAsDeliveryCheckbox) {
            sameAsDeliveryCheckbox.addEventListener("change", () => {
                this.handleSameAsDelivery(sameAsDeliveryCheckbox.checked);
            });
        }
    }

    private showManualEntry(targetPrefix: string): void {
        const lookupContainer = document.querySelector(`#loqate-${targetPrefix}-address, [id^="loqate-${targetPrefix}-"]`) as HTMLElement;
        const manualContainer = document.getElementById(`${targetPrefix}-manual-address`) as HTMLElement;

        if (lookupContainer && manualContainer) {
            lookupContainer.style.display = 'none';
            manualContainer.style.display = 'block';
        }
    }

    private showAddressLookup(targetPrefix: string): void {
        const lookupContainer = document.querySelector(`#loqate-${targetPrefix}-address, [id^="loqate-${targetPrefix}-"]`) as HTMLElement;
        const manualContainer = document.getElementById(`${targetPrefix}-manual-address`) as HTMLElement;

        if (lookupContainer && manualContainer) {
            lookupContainer.style.display = 'block';
            manualContainer.style.display = 'none';
        }
    }

    private handleAddressLookupInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const container = input.closest(".addresslookup-container") as HTMLElement;
        if (!container) return;

        const containerId = container.id;
        const query = input.value.trim();

        // Clear existing timer
        if (this.locationTimer[containerId]) {
            window.clearTimeout(this.locationTimer[containerId]);
        }

        // Don't search for very short queries
        if (query.length < 3) {
            this.hideResults(container);
            return;
        }

        // Don't repeat the same query
        if (this.lastQuery[containerId] === query) {
            return;
        }

        // Set a timer to perform the search after 800ms of no typing
        this.locationTimer[containerId] = window.setTimeout(() => {
            this.performAddressSearch(container, query);
            this.lastQuery[containerId] = query;
        }, 800);
    }

    private async performAddressSearch(container: HTMLElement, query: string): Promise<void> {
        this.showLoading(container);
        this.hideResults(container);

        try {
            const response = await fetch(`/umbraco/surface/AddressInputSurface/GetLocation?s=${encodeURIComponent(query)}`);
            const addresses: AddressData[] = await response.json();

            this.displayAddressResults(container, addresses);
        } catch (error) {
            console.error('Address lookup error:', error);
            this.showError(container);
        } finally {
            this.hideLoading(container);
        }
    }

    private displayAddressResults(container: HTMLElement, addresses: AddressData[]): void {
        const resultsContainer = container.querySelector(".addresslookup-collapse-results") as HTMLElement;
        const resultsList = container.querySelector(".addresslookup-results-list ul") as HTMLUListElement;
        const noResults = container.querySelector(".addresslookup-results-none") as HTMLElement;
        const resultListContainer = container.querySelector(".addresslookup-results-list") as HTMLElement;

        if (!resultsContainer || !resultsList) return;

        // Clear previous results
        resultsList.innerHTML = '';

        if (addresses.length === 0) {
            noResults.style.display = 'block';
            resultListContainer.style.display = 'none';
        } else {
            noResults.style.display = 'none';
            resultListContainer.style.display = 'block';

            addresses.forEach(address => {
                const li = document.createElement('li');
                li.className = 'addresslookup-result-item';
                li.dataset.id = address.id;
                li.dataset.isContainer = String(address.type === 'Container' || address.isContainer || false);

                // Combine text and description into one formatted address
                const fullAddress = [address.text, address.description]
                    .filter(part => part) // Remove empty parts
                    .join(', ');

                li.textContent = fullAddress;
                resultsList.appendChild(li);
            });
        }

        resultsContainer.style.display = 'block';
        resultsContainer.classList.add('show');
    }

    private async handleAddressSelection(event: Event): Promise<void> {
        const li = event.target as HTMLElement;
        const listItem = li.closest('li') as HTMLLIElement;
        if (!listItem) return;

        const loqateId = listItem.dataset.id;
        if (!loqateId) return;

        const container = listItem.closest(".addresslookup-container") as HTMLElement;
        if (!container) return;

        // Check if this is a container that needs to be drilled down into
        const isContainer = listItem.dataset.isContainer === 'true';
        if (isContainer) {
            await this.drillDownContainer(container, loqateId);
            return;
        }

        // Store the Loqate ID
        const loqateIdField = container.querySelector(".js-address-loqate-id-field") as HTMLInputElement;
        if (loqateIdField) {
            loqateIdField.value = loqateId;
        }

        // Hide results
        this.hideResults(container);

        // Fetch and populate full address details
        await this.populateFullAddress(container, loqateId, listItem);
    }

    private async drillDownContainer(container: HTMLElement, containerId: string): Promise<void> {
        this.showLoading(container);

        try {
            const response = await fetch(`/umbraco/surface/AddressInputSurface/DrillDownContainer?containerId=${encodeURIComponent(containerId)}`);
            const addresses: AddressData[] = await response.json();

            this.displayAddressResults(container, addresses);
        } catch (error) {
            console.error('Drill-down error:', error);
            this.showError(container);
        } finally {
            this.hideLoading(container);
        }
    }

    private async populateFullAddress(container: HTMLElement, loqateId: string, listItem: HTMLLIElement): Promise<void> {
        try {
            const response = await fetch(`/umbraco/surface/AddressInputSurface/GetFullAddress?loqateId=${encodeURIComponent(loqateId)}`);
            const address: FullAddress = await response.json();

            // Determine which set of fields to populate based on container
            const containerName = container.dataset.name || '';
            const isDelivery = containerName.toLowerCase().includes('delivery') || container.id.includes('delivery');
            const fieldPrefix = isDelivery ? '' : 'Billing';

            // Populate form fields
            this.setFieldValue(`${fieldPrefix}AddressLine1`, address.line1 || '');
            this.setFieldValue(`${fieldPrefix}AddressLine2`, address.line2 || address.line3 || '');
            this.setFieldValue(`${fieldPrefix}Town`, address.line3 || '');
            this.setFieldValue(`${fieldPrefix}City`, address.city || '');
            this.setFieldValue(`${fieldPrefix}Postcode`, address.postalCode || '');
            this.setFieldValue(`${fieldPrefix}Country`, address.countryName || 'United Kingdom');

            // Show nicely formatted selected address
            const selectedContainer = container.querySelector(".addresslookup-collapse-select") as HTMLElement;
            const selectedText = container.querySelector(".addresslookup-selected p") as HTMLParagraphElement;
            if (selectedContainer && selectedText) {
                // Format the address properly with the detailed data
                const addressParts = [
                    address.line1,
                    address.line2,
                    address.line3,
                    address.city,
                    address.postalCode
                ].filter(part => part && part.trim()); // Remove empty parts

                selectedText.textContent = addressParts.join(', ');
                selectedContainer.style.display = 'block';
                selectedContainer.classList.add('show');
            }

            // Hide the lookup hint message once address is selected
            const hintMessage = document.querySelector('.js-address-lookup-hint') as HTMLElement;
            if (hintMessage) {
                hintMessage.style.display = 'none';
            }

        } catch (error) {
            console.error('Error fetching full address:', error);

            // Fallback to showing the list item text if API fails
            const selectedContainer = container.querySelector(".addresslookup-collapse-select") as HTMLElement;
            const selectedText = container.querySelector(".addresslookup-selected p") as HTMLParagraphElement;
            if (selectedContainer && selectedText) {
                selectedText.textContent = listItem.textContent || '';
                selectedContainer.style.display = 'block';
                selectedContainer.classList.add('show');
            }
        }
    }

    private setFieldValue(fieldName: string, value: string): void {
        // Try by ID first
        let field = document.getElementById(fieldName) as HTMLInputElement;

        // Try by name if ID doesn't work
        if (!field) {
            field = document.querySelector(`[name="${fieldName}"]`) as HTMLInputElement;
        }

        // Try lowercase ID
        if (!field) {
            field = document.getElementById(fieldName.toLowerCase()) as HTMLInputElement;
        }

        if (field) {
            field.value = value;
            field.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    private handleSameAsDelivery(checked: boolean): void {
        const billingDetails = document.getElementById('billingDetails') as HTMLElement;
        if (!billingDetails) return;

        if (checked) {
            billingDetails.classList.remove('active');
            billingDetails.style.display = 'none';

            // Copy delivery address to billing
            this.copyFieldValue('Salutation', 'BillingSalutation');
            this.copyFieldValue('FirstName', 'BillingFirstName');
            this.copyFieldValue('LastName', 'BillingLastName');
            this.copyFieldValue('Organisation', 'BillingOrganisation');
            this.copyFieldValue('AddressLine1', 'BillingAddressLine1');
            this.copyFieldValue('AddressLine2', 'BillingAddressLine2');
            this.copyFieldValue('Town', 'BillingTown');
            this.copyFieldValue('City', 'BillingCity');
            this.copyFieldValue('Postcode', 'BillingPostcode');
            this.copyFieldValue('Country', 'BillingCountry');

            // Disable required validation on hidden billing fields
            this.disableRequiredFields(billingDetails);
        } else {
            billingDetails.classList.add('active');
            billingDetails.style.display = 'block';

            // Re-enable required validation on billing fields
            this.enableRequiredFields(billingDetails);
        }
    }

    private disableRequiredFields(container: HTMLElement): void {
        const requiredFields = container.querySelectorAll('[required]') as NodeListOf<HTMLInputElement>;
        requiredFields.forEach(field => {
            field.removeAttribute('required');
            field.dataset.wasRequired = 'true';
        });
    }

    private enableRequiredFields(container: HTMLElement): void {
        const fields = container.querySelectorAll('[data-was-required="true"]') as NodeListOf<HTMLInputElement>;
        fields.forEach(field => {
            field.setAttribute('required', '');
            delete field.dataset.wasRequired;
        });
    }

    private copyFieldValue(sourceId: string, targetId: string): void {
        const sourceField = document.getElementById(sourceId) as HTMLInputElement | HTMLSelectElement;
        const targetField = document.getElementById(targetId) as HTMLInputElement | HTMLSelectElement;

        if (sourceField && targetField) {
            targetField.value = sourceField.value;
            targetField.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    private showLoading(container: HTMLElement): void {
        const loading = container.querySelector(".addresslookup-loading") as HTMLElement;
        if (loading) loading.style.display = 'block';
    }

    private hideLoading(container: HTMLElement): void {
        const loading = container.querySelector(".addresslookup-loading") as HTMLElement;
        if (loading) loading.style.display = 'none';
    }

    private hideResults(container: HTMLElement): void {
        const results = container.querySelector(".addresslookup-collapse-results") as HTMLElement;
        if (results) {
            results.style.display = 'none';
            results.classList.remove('show');
        }
    }

    private showError(container: HTMLElement): void {
        const resultsContainer = container.querySelector(".addresslookup-collapse-results") as HTMLElement;
        const noResults = container.querySelector(".addresslookup-results-none") as HTMLElement;
        const resultList = container.querySelector(".addresslookup-results-list") as HTMLElement;

        if (noResults && resultList && resultsContainer) {
            noResults.textContent = 'An error occurred while searching. Please try again or enter your address manually.';
            noResults.style.display = 'block';
            resultList.style.display = 'none';
            resultsContainer.style.display = 'block';
            resultsContainer.classList.add('show');
        }
    }
}

// Initialize on page load
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        const service = new CheckoutAddressService();
        service.initialize();
    });
} else {
    const service = new CheckoutAddressService();
    service.initialize();
}
